import { ActionIcon, Anchor, Button, Tooltip } from '@mantine/core';
import {
  IconDownload,
  IconExternalLink,
  IconInfoCircle,
  IconPaperclip,
  IconPrinter,
  IconStar,
  IconStarFilled,
} from '@tabler/icons-react';
import { type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../format.js';
import type { MenuScope } from '../../menu.js';
import { Panel } from '../../Panel.js';
import { followLink, useShell } from '../../shell-context.js';
import classes from './Documents.module.css';
import { formatFileSize, revisionBare, sectionHref } from './library.js';
import { FileKindIcon } from './parts.js';
import type { DocumentPermissions } from './source.js';
import type { LibraryDetail, LibraryDocument, LibraryRevision, LibrarySection } from './types.js';

/** Ofisin Form History ekranı (menu-yapisi.md "ISM Forms ▸ Form History"). */
const FORM_HISTORY_PATH = '/compliance/ism-forms/history';

export interface DocumentSidebarProps {
  section: LibrarySection;
  scope: MenuScope;
  detail: LibraryDetail;
  revision: LibraryRevision | null;
  permissions: DocumentPermissions;
  canOutput: boolean;
  /** Pasif Download/Print'in nedeni (ipucu ve ekran okuyucu; inceleme M8a3-R13). */
  outputHint?: string | null | undefined;
  /** Önizleyici yok (gemide dosya henüz gemide değil): Open Document pasif (M8a3-R12). */
  openBlocked?: boolean | undefined;
  offsetMin?: number | undefined;
  /** Open Document: önizlemeyi tam ekran açar (prosedür ve manual). */
  onOpen: () => void;
  onDownload: () => void;
  onPrint: () => void;
  /** Favori; ofiste gemi kütüphanesinde yok (salt okunur; M8a4e). */
  onFavorite?: (() => void) | undefined;
  onDownloadRelated: (document: LibraryDocument) => void;
  /**
   * Gemide kayıt açabilen kişide ISM formunun "How to fill in" kartı (sahip kararı 2026-10-17;
   * ADR 0074): Form Information ve Actions kartlarının yerine tek kart. Bilgi Form Details'in
   * bilgi şeridinde, birincil eylem başlıkta. Verilmezse (ofis, yetkisiz gemi) eski düzen.
   */
  formActions?: { guide: ReactNode } | null | undefined;
}

/**
 * Sağ sütun (gemi-ism-formlari.jpeg): "Form Information" (etiket üstte, değer altta, aralarında
 * çizgi), "Actions" (Open Form, Download Form, Print Form, Add to Favorites; tam genişlik) ve
 * "Related Documents" (tür ikonu, ad, boyut, indirme). Open Form form kayıtlarıyla M8b'de
 * gelir; pasiftir ve nedeni ipucunda ve ekran okuyucuda yazar.
 */
export function DocumentSidebar({
  section,
  scope,
  detail,
  revision,
  permissions,
  canOutput,
  outputHint = null,
  openBlocked = false,
  offsetMin,
  onOpen,
  onDownload,
  onPrint,
  onFavorite,
  onDownloadRelated,
  formActions = null,
}: DocumentSidebarProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const shell = useShell();
  const labels = (key: string) => t(`documents.sections.${section}.${key}`);
  const hintId = useId();
  const outputId = useId();
  const isForm = detail.kind === 'form';
  const officeForm = isForm && !formActions && scope === 'web';
  const favorite = onFavorite ? (
    <Button
      variant="default"
      fullWidth
      className={classes.actionButton}
      leftSection={
        detail.favorite ? (
          <IconStarFilled size={16} className={classes.favoriteOn} />
        ) : (
          <IconStar size={16} />
        )
      }
      aria-pressed={detail.favorite}
      onClick={onFavorite}
    >
      {detail.favorite ? t('documents.actions.favoriteRemove') : t('documents.actions.favoriteAdd')}
    </Button>
  ) : null;
  const openHint = t(officeForm ? 'documents.actions.officeHint' : 'documents.actions.openHint');
  const outputReason = !canOutput ? outputHint : null;

  const fact = (label: string, value: ReactNode, title?: string) => (
    <div className={classes.sideFact}>
      <dt>{label}</dt>
      <dd title={title}>{value}</dd>
    </div>
  );
  const output = (label: string, icon: ReactNode, onClick: () => void) => {
    const button = (
      <Button
        variant="default"
        fullWidth
        className={classes.actionButton}
        leftSection={icon}
        disabled={!revision || !canOutput}
        aria-describedby={outputReason ? outputId : undefined}
        onClick={onClick}
      >
        {label}
      </Button>
    );
    return outputReason ? (
      <Tooltip label={outputReason} withArrow>
        <span className={classes.block}>{button}</span>
      </Tooltip>
    ) : (
      button
    );
  };

  return (
    <>
      {isForm && formActions ? (
        <Panel title={t('forms.page.title')} className={classes.sideCard}>
          {formActions.guide}
          {favorite ? <div className={classes.guideFavorite}>{favorite}</div> : null}
        </Panel>
      ) : (
        <>
          <Panel title={labels('information')} className={classes.sideCard}>
            <dl className={classes.sideFacts}>
              {fact(labels('number'), detail.docNo)}
              {fact(labels('name'), detail.title, detail.title)}
              {fact(t('documents.fields.category'), detail.folderPath.at(-1)?.name ?? '—')}
              {fact(
                t('documents.fields.fileType'),
                revision ? (
                  <span className={classes.sideType}>
                    <FileKindIcon kind={revision.fileKind} size={20} />
                    {t(`documents.fileKindsLong.${revision.fileKind}`)}
                  </span>
                ) : (
                  '—'
                ),
              )}
              {fact(
                t('documents.fields.version'),
                revision ? revisionBare(revision.revisionNo) : '—',
              )}
              {fact(
                t('documents.fields.lastUpdated'),
                formatDateTime(revision?.publishedAt ?? revision?.createdAt, offsetMin),
              )}
              {fact(t('documents.fields.createdBy'), revision?.createdByName ?? '—')}
              <div className={classes.sideFact} data-wide>
                <dt>{t('documents.fields.description')}</dt>
                <dd className={classes.descriptionBox}>
                  {detail.description ?? (
                    <span className={classes.muted}>{t('documents.fields.noDescription')}</span>
                  )}
                </dd>
              </div>
            </dl>
          </Panel>

          <Panel title={t('documents.actions.title')} className={classes.sideCard}>
            <div className={classes.actionList}>
              {isForm ? (
                <Tooltip label={openHint} withArrow>
                  <span className={classes.block}>
                    <Button
                      fullWidth
                      className={`${classes.actionButton} ${classes.openButton}`}
                      leftSection={<IconExternalLink size={16} />}
                      disabled
                      aria-describedby={hintId}
                    >
                      {labels('open')}
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Button
                  fullWidth
                  className={classes.actionButton}
                  leftSection={<IconExternalLink size={16} />}
                  disabled={!revision || openBlocked}
                  onClick={onOpen}
                >
                  {/* Prosedür ve manual "Open Document" (ISM Forms'ta da; inceleme S7) */}
                  {t('documents.actions.openDocument')}
                </Button>
              )}
              {isForm ? (
                <span id={hintId} className={classes.srOnly}>
                  {openHint}
                </span>
              ) : null}
              {officeForm ? (
                // Ofiste kayıt açılmaz (ADR 0042: kayıt gemide girilir); ekran bunu açıkça söyler
                // (ortağın geri bildirimi 2026-10-13)
                <div className={classes.officeNote} role="note">
                  <IconInfoCircle size={16} aria-hidden="true" />
                  <p>
                    {t('documents.actions.officeRecords')}{' '}
                    <Anchor
                      component="button"
                      type="button"
                      className={classes.officeNoteLink}
                      onClick={() => shell?.navigate(FORM_HISTORY_PATH)}
                    >
                      {t('documents.actions.formHistory')}
                    </Anchor>
                  </p>
                </div>
              ) : null}
              {permissions.export
                ? output(labels('download'), <IconDownload size={16} />, onDownload)
                : null}
              {permissions.export
                ? output(labels('print'), <IconPrinter size={16} />, onPrint)
                : null}
              {permissions.export && outputReason ? (
                <span id={outputId} className={classes.srOnly}>
                  {outputReason}
                </span>
              ) : null}
              {favorite}
            </div>
          </Panel>
        </>
      )}

      <Panel
        title={t('documents.related.title')}
        icon={<IconPaperclip size={17} stroke={1.8} />}
        className={classes.sideCard}
      >
        {detail.relations.length === 0 ? (
          <p className={classes.muted}>{t('documents.related.none')}</p>
        ) : (
          <ul className={classes.related}>
            {detail.relations.map((related) => {
              const file = related.current?.file;
              const path = sectionHref(related.section, { doc: related.id });
              const name = file?.name ?? related.title;
              return (
                <li key={related.id} className={classes.relatedRow}>
                  <FileKindIcon
                    kind={related.current?.fileKind ?? 'pdf'}
                    variant="glyph"
                    size={20}
                  />
                  <a
                    href={path}
                    className={classes.relatedName}
                    title={name}
                    onClick={(event) => shell && followLink(event, path, shell.navigate)}
                  >
                    {name}
                  </a>
                  <span className={classes.relatedSize}>
                    {file ? formatFileSize(file.size, locale) : t('documents.related.notAvailable')}
                  </span>
                  {file && permissions.export ? (
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      aria-label={t('documents.related.download', { name })}
                      onClick={() => onDownloadRelated(related)}
                    >
                      <IconDownload size={15} />
                    </ActionIcon>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
    </>
  );
}
